import qs from 'qs'

import { locateCountry } from '@last/core'

import { useLocationsStore } from '@/modules/locations/store/locations'
import {
  AssistantApi,
  AvailabilityApi,
  Configuration,
  CustomersApi,
  DevicesApi,
  EmployeesApi,
  FloorplanApi,
  HardwareStoreApi,
  ImportCatalogApi,
  InternalBillingApi,
  LegalEntitiesApi,
  LocationApi,
  LocationCatalogApi,
  NotificationsApi,
  OrganizationApi,
  OrganizationCatalogApi,
  PaymentsApi,
  PaymentsReportsApi,
  QrApi,
  ReportsApi,
  ReservationsApi,
  ShiftsApi,
  ShopApi,
  TemporalClosuresApi,
  TranslationsCatalogApi,
  UsersApi
} from '@/openapi'
import { useAuthStore } from '@/store/auth'

function isOkayResponse({ status }: Response) {
  return status >= 200 && status < 400
}

const decoder = new TextDecoder()
async function parseError(response: Response) {
  if (!response.body) return
  const res = await response.body.getReader().read()
  return JSON.parse(decoder.decode(res.value))
}

const country = locateCountry()

const configuration = new Configuration({
  basePath: `${import.meta.env.VITE_API_BASE_URL}/admin`,
  queryParamsStringify: params => qs.stringify(params),
  middleware: [
    {
      pre: async ctx => {
        const auth = useAuthStore()
        const locations = useLocationsStore()
        if (auth.accessToken && locations.selectedLocationId && locations.selectedOrganizationId) {
          ctx.init.headers = {
            ...ctx.init.headers,
            Authorization: auth.accessToken,
            'Location-ID': locations.selectedLocationId,
            'Organization-ID': locations.selectedOrganizationId
          }
        } else if (auth.accessToken && locations.selectedOrganizationId) {
          ctx.init.headers = {
            ...ctx.init.headers,
            Authorization: auth.accessToken,
            'Organization-ID': locations.selectedOrganizationId
          }
        } else if (auth.accessToken) {
          ctx.init.headers = {
            ...ctx.init.headers,
            Authorization: auth.accessToken
          }
        }
        const countryCode = await country
        if (countryCode) {
          ctx.init.headers = {
            ...ctx.init.headers,
            'Country-Code': countryCode
          }
        }

        const userId = auth.user.id
        if (userId) {
          ctx.init.headers = {
            ...ctx.init.headers,
            'Datadog-User-ID': userId
          }
        }
        return ctx
      },
      post: async ctx => {
        try {
          if (isOkayResponse(ctx.response)) return
          else return parseError(ctx.response)
        } catch {
          // no-op
        }
      }
    }
  ]
})

export const reservationAvailabilityApi = new AvailabilityApi(configuration)
export const bookingsPaymentsApi = new PaymentsApi(configuration)
export const shiftsApi = new ShiftsApi(configuration)
export const floorplanApi = new FloorplanApi(configuration)
export const organizationCatalogApi = new OrganizationCatalogApi(configuration)
export const locationCatalogApi = new LocationCatalogApi(configuration)
export const translationsCatalogApi = new TranslationsCatalogApi(configuration)
export const organizationApi = new OrganizationApi(configuration)
export const locationApi = new LocationApi(configuration)
export const notificationsApi = new NotificationsApi(configuration)
export const reservationsApi = new ReservationsApi(configuration)
export const internalBillingApi = new InternalBillingApi(configuration)
export const hardwareStoreApi = new HardwareStoreApi(configuration)
export const employeesApi = new EmployeesApi(configuration)
export const reportsApi = new ReportsApi(configuration)
export const paymentsReportsApi = new PaymentsReportsApi(configuration)
export const legalEntitiesApi = new LegalEntitiesApi(configuration)
export const usersApi = new UsersApi(configuration)
export const assistantApi = new AssistantApi(configuration)
export const temporalClosuresApi = new TemporalClosuresApi(configuration)
export const customerApi = new CustomersApi(configuration)
export const qrApi = new QrApi(configuration)
export const importCatalogApi = new ImportCatalogApi(configuration)
export const shopApi = new ShopApi(configuration)
export const devicesApi = new DevicesApi(configuration)
